import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Compass, Shield, Lightbulb, HeartHandshake, TrendingUp } from 'lucide-react';
const journey = [{
  title: 'Director of Finance & IT Services',
  body: 'Began her healthcare leadership as a finance executive, overseeing financial strategy, audit leadership, and Medicaid initiatives.'
}, {
  title: 'Chief Operating Officer',
  body: 'Led statewide collaboration initiatives and workforce solutions, driving operational excellence across member organizations.'
}, {
  title: 'Executive Director',
  body: 'Founded and built a statewide association from inception, establishing its governance framework and advocacy agenda.'
}, {
  title: 'Government Relations Executive',
  body: 'Directed legislative strategy and stakeholder engagement, advancing healthcare policy at the state and federal levels.'
}, {
  title: 'Chief Executive Officer',
  body: 'As CEO of the Utah Medical Association, she scaled membership, revenue, and impact while championing physician advocacy.'
}];
const values = [{
  icon: Shield,
  title: 'Integrity',
  body: 'Leading with transparency and unwavering ethical standards.'
}, {
  icon: Target,
  title: 'Excellence',
  body: 'Setting a high bar for performance and measurable outcomes.'
}, {
  icon: HeartHandshake,
  title: 'Accountability',
  body: 'Owning results and serving stakeholders with diligence.'
}, {
  icon: Lightbulb,
  title: 'Innovation',
  body: 'Pursuing creative solutions to complex healthcare challenges.'
}, {
  icon: Compass,
  title: 'Advocacy',
  body: 'Championing physicians, patients, and the healthcare community.'
}, {
  icon: TrendingUp,
  title: 'Strategic Thinking',
  body: 'Aligning vision with disciplined execution and growth.'
}];
export function About() {
  useEffect(() => {
    document.title = 'About Michelle McOmber | Healthcare Executive Biography';
  }, []);
  return <div className="w-full bg-white" data-id="element-102">
      <header className="bg-navy-900 text-white py-20" data-id="element-103">
        <div className="max-w-7xl mx-auto px-6" data-id="element-104">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-105">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-white" data-id="element-106">
              About Michelle
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl" data-id="element-107">
              A healthcare executive whose career reflects collaborative
              leadership, advocacy, and a lifelong commitment to advancing
              healthcare communities.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Leadership journey */}
      <section className="py-20" data-id="element-108">
        <div className="max-w-7xl mx-auto px-6" data-id="element-109">
          <div className="flex flex-col lg:flex-row gap-16" data-id="element-110">
            <div className="lg:w-1/3" data-id="element-111">
              <div className="sticky top-32" data-id="element-112">
                <div className="relative mb-8 max-w-xs" data-id="element-113">
                  <div className="absolute -inset-px bg-gold translate-x-3 translate-y-3" data-id="element-114" />
                  <img src="https://cdn.magicpatterns.com/uploads/8aVXY2pBvqpYt8negkm5zj/Michelle_Picture.jpg" alt="Portrait of Michelle McOmber" className="relative w-full aspect-[4/5] object-cover object-center shadow-sm" data-id="element-115" />
                </div>
                <span className="text-gold font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-116">
                  Leadership Journey
                </span>
                <h2 className="text-3xl font-serif font-bold text-navy-900 mb-6" data-id="element-117">
                  From Finance Leader to CEO
                </h2>
                <p className="text-slate-600 mb-8" data-id="element-118">
                  Michelle&rsquo;s progression through finance, operations, and
                  executive leadership has shaped a uniquely well-rounded
                  healthcare executive.
                </p>
              </div>
            </div>

            <div className="lg:w-2/3" data-id="element-119">
              <div className="space-y-8 relative before:absolute before:left-5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200" data-id="element-120">
                {journey.map((step, i) => <motion.div key={step.title} initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: i * 0.08
              }} className="relative pl-16" data-id="element-121">
                    <span className="absolute left-0 top-0 flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-gold text-white shadow" data-id="element-122">
                      <span className="w-2 h-2 bg-white rounded-full" data-id="element-123" />
                    </span>
                    <div className="p-6 rounded-lg border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-124">
                      <h3 className="font-bold text-navy-900 text-xl mb-2" data-id="element-125">
                        {step.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed" data-id="element-126">
                        {step.body}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy & Mission */}
      <section className="py-20 bg-slate-50" data-id="element-127">
        <div className="max-w-7xl mx-auto px-6" data-id="element-128">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-129">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-130">
              <h2 className="text-3xl font-serif font-bold text-navy-900 mb-8" data-id="element-131">
                Executive Philosophy
              </h2>
              <div className="prose prose-lg text-slate-600 space-y-4" data-id="element-132">
                <p data-id="element-133">
                  Michelle leads through{' '}
                  <strong data-id="element-134">collaborative leadership</strong> &mdash; building
                  consensus among diverse stakeholders to achieve shared goals.
                </p>
                <p data-id="element-135">
                  She believes effective <strong data-id="element-136">advocacy</strong> is not just
                  about policy; it&rsquo;s about protecting the sacred
                  relationship between providers and patients. By fostering{' '}
                  <strong data-id="element-137">innovation</strong> and pursuing strategic{' '}
                  <strong data-id="element-138">growth</strong>, she builds organizations that endure.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative bg-navy-900 text-white p-10 rounded-xl overflow-hidden" data-id="element-139">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-140" />
              <Compass className="w-12 h-12 text-gold mb-6 relative z-10" data-id="element-141" />
              <h2 className="text-2xl font-serif font-bold mb-4 relative z-10 text-white" data-id="element-142">
                Personal Mission Statement
              </h2>
              <p className="text-xl leading-relaxed font-light relative z-10" data-id="element-143">
                To improve healthcare outcomes through visionary leadership and
                policy advancement &mdash; serving physicians, patients, and the
                communities that depend on a strong healthcare system.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="py-24 bg-white" data-id="element-144">
        <div className="max-w-7xl mx-auto px-6" data-id="element-145">
          <div className="text-center mb-16" data-id="element-146">
            <h2 className="text-3xl font-serif font-bold text-navy-900" data-id="element-147">
              Core Leadership Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-148">
            {values.map((v, i) => <motion.div key={v.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className="group p-8 rounded-xl border border-slate-100 hover:shadow-executive transition-shadow" data-id="element-149">
                <v.icon className="w-10 h-10 text-navy-900 group-hover:text-gold transition-colors mb-6" data-id="element-150" />
                <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-151">
                  {v.title}
                </h3>
                <p className="text-slate-600" data-id="element-152">{v.body}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}